export const positioning = 'Intelligence infrastructure for your Product & GTM';
export const siteDescription =
  'Foxet turns product knowledge and customer context into expert conversations, guided experiences and connected action across sales, onboarding and support.';
export const navLinks = [
  { label: 'Platform', href: '/platform' },
  { label: 'Solutions', href: '/#solutions' },
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'Enterprise', href: '/enterprise' },
];
/** What a visitor can use today versus the direction the platform is taking. */
export type Availability = 'live' | 'direction';
export const solutions = {
  product: {
    label: 'For Product',
    availability: 'live' as Availability,
    eyebrow: 'Product teams',
    title: 'Your product knowledge. A living advantage.',
    description:
      'Make the expertise behind your product useful to everyone who explains, evaluates and uses it. Bring customer questions and friction back to the people building it.',
    outcome:
      'A closer connection between what you build and what customers understand.',
    capabilities: [
      [
        'Build a shared understanding',
        'Bring product flows, documentation and your team’s expertise into a common foundation.',
      ],
      [
        'Shape the product truth',
        'Review the knowledge, curate the workflows and decide what Foxet can share with customers.',
      ],
      [
        'Listen to the market',
        'Surface recurring questions, objections and unmet needs from customer conversations.',
      ],
    ],
    question: 'Which product capabilities do buyers keep asking about?',
    insight:
      'Bring repeated questions and unclear workflows into your next product discussion.',
  },
  sales: {
    label: 'For Sales & Pre-sales',
    availability: 'live' as Availability,
    eyebrow: 'Sales & pre-sales',
    title: 'Product expertise that moves the conversation forward.',
    description:
      'Help buyers explore the product in the context of their own work. Give your team the questions, requirements and next steps that make the next conversation more useful.',
    outcome: 'From a product question to a relevant, owned next step.',
    capabilities: [
      [
        'Understand the buying context',
        'Capture the prospect’s role, current process and desired outcome before choosing what to show.',
      ],
      [
        'Make the product relevant',
        'Answer questions and demonstrate approved workflows around the buyer’s priorities.',
      ],
      [
        'Carry the conversation forward',
        'Bring briefs, objections and follow-up actions into your team’s sales workflow.',
      ],
    ],
    question: 'Can we manage approvals across different regions?',
    insight:
      'A relevant workflow, the buyer’s evaluation criteria and a next step for the account team.',
  },
  marketing: {
    label: 'For Marketing',
    availability: 'live' as Availability,
    eyebrow: 'Marketing teams',
    title: 'Turn product interest into a real conversation.',
    description:
      'Give visitors a way to ask, explore and understand your product when their interest is highest. Make your product expertise part of the website experience.',
    outcome:
      'A more useful first interaction for your visitors and your sales team.',
    capabilities: [
      [
        'Meet the question behind the visit',
        'Let visitors explore their own use case through product conversations and walkthroughs.',
      ],
      [
        'Connect the experience',
        'Bring published product experiences into your website and campaign journeys.',
      ],
      [
        'Learn what resonates',
        'Use buyer questions and product interest to sharpen your content and sales conversations.',
      ],
    ],
    question: 'How would this work for a team like ours?',
    insight:
      'The visitor’s use case becomes a relevant product experience and context for the team.',
  },
  training: {
    label: 'For Onboarding & Training',
    availability: 'direction' as Availability,
    eyebrow: 'Onboarding & training · direction',
    title: 'Help every user find their way to value.',
    description:
      'Direction, not shipped: onboarding and training draw on the same product brain as pre-sales and demonstrations. Scope is agreed with your team.',
    outcome: 'Product knowledge that stays useful after the purchase.',
    capabilities: [
      [
        'Start with the task',
        'Organise guidance around the workflows a customer needs to complete.',
      ],
      [
        'Reuse your product expertise',
        'Build guidance on the same curated knowledge and product flows used during evaluation.',
      ],
      [
        'Keep your team connected',
        'Use questions and friction to identify where customers need more help.',
      ],
    ],
    question: 'How do I set up our first approval workflow?',
    insight:
      'Relevant guidance, a clear task and context for the team when a customer needs help.',
  },
  support: {
    label: 'For Customer Success & Support',
    availability: 'direction' as Availability,
    eyebrow: 'Customer success & support · direction',
    title: 'Product answers that help customers move forward.',
    description:
      'Direction, not shipped: grounded answers, guided steps and a human handoff on the same product brain. Scope is agreed with your team.',
    outcome: 'A connected experience from product question to useful help.',
    capabilities: [
      [
        'Ground the answer in the product',
        'Use reviewed product knowledge to explain capabilities, constraints and workflows.',
      ],
      [
        'Make guidance practical',
        'Help customers understand the relevant steps and the conditions that affect their task.',
      ],
      [
        'Bring in the right person',
        'Carry the question and context into the team’s next action when human help is needed.',
      ],
    ],
    question: 'Why can’t I change this approval rule?',
    insight:
      'An explanation grounded in the product, with a clear path to the appropriate human help.',
  },
} as const;
export type SolutionKey = keyof typeof solutions;
